"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronsUpDown } from "lucide-react";
import { Input as AriaInput, type InputProps as AriaInputProps, ListBox as AriaListBox, type ListBoxProps as AriaListBoxProps, type PopoverProps as AriaPopoverProps, type ValidationResult as AriaValidationResult, ComboBox, type ComboBoxProps, composeRenderProps, Text } from "react-aria-components";
import { cn } from "@/utils/cn";
import { Button } from "./button";
import { FieldError, FieldGroup, Label } from "./field";
import { ListBoxCollection, ListBoxHeader, ListBoxItem, ListBoxSection } from "./list-box";
import { Popover } from "./popover";
const ComboboxItem = ListBoxItem;
const _ComboboxHeader = ListBoxHeader;
const _ComboboxSection = ListBoxSection;
const _ComboboxCollection = ListBoxCollection;
const ComboboxInput = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = composeRenderProps(className, _temp);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AriaInput className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const ComboboxPopover = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = composeRenderProps(className, _temp2);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Popover placement="bottom" shouldFlip={true} shouldUpdatePosition={true} className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const ComboboxListBox = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = composeRenderProps(className, _temp3);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AriaListBox className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
interface JollyComboBoxProps<T extends object> extends Omit<ComboBoxProps<T>, "children"> {
  label?: string;
  description?: string | null;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  children: React.ReactNode | ((item: T) => React.ReactNode);
  placeholder?: string;
}
function JollyComboBox(t0) {
  const $ = _c(31);
  let children;
  let className;
  let description;
  let errorMessage;
  let label;
  let placeholder;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      description,
      errorMessage,
      className,
      children,
      placeholder,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = description;
    $[4] = errorMessage;
    $[5] = label;
    $[6] = placeholder;
    $[7] = props;
  } else {
    children = $[1];
    className = $[2];
    description = $[3];
    errorMessage = $[4];
    label = $[5];
    placeholder = $[6];
    props = $[7];
  }
  let t1;
  if ($[8] !== className) {
    t1 = composeRenderProps(className, _temp4);
    $[8] = className;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== label) {
    t2 = <Label>{label}</Label>;
    $[10] = label;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== placeholder) {
    t3 = <ComboboxInput placeholder={placeholder} />;
    $[12] = placeholder;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Button variant="ghost" size="icon" className="mr-1 size-6 p-1" aria-label="Show options"><ChevronsUpDown aria-hidden="true" className="size-4 opacity-50" /></Button>;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t3) {
    t5 = <FieldGroup className="p-0">{t3}{t4}</FieldGroup>;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== description) {
    t6 = description && <Text className="text-sm text-muted-foreground" slot="description">{description}</Text>;
    $[17] = description;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== errorMessage) {
    t7 = <FieldError>{errorMessage}</FieldError>;
    $[19] = errorMessage;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== children) {
    t8 = <ComboboxPopover><ComboboxListBox>{children}</ComboboxListBox></ComboboxPopover>;
    $[21] = children;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== props || $[24] !== t1 || $[25] !== t2 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8) {
    t9 = <ComboBox className={t1} {...props}>{t2}{t5}{t6}{t7}{t8}</ComboBox>;
    $[23] = props;
    $[24] = t1;
    $[25] = t2;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function _temp4(className_0) {
  return cn("group flex flex-col gap-2", className_0);
}
export { ComboboxItem, JollyComboBox };
function _temp(className_0) {
  return cn("flex h-10 w-full bg-background px-3 py-2 outline-none file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground", "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50", className_0);
}
function _temp2(className_0) {
  return cn("w-[calc(var(--trigger-width)+4px)]", className_0);
}
function _temp3(className_0) {
  return cn("max-h-[inherit] overflow-auto p-1 outline-none [clip-path:inset(0_0_0_0_round_calc(var(--radius)-2px))]", className_0);
}